import {z} from "zod";
import {TtsModeSchema} from "@/src/shared/tts";

export const RANDOM_EFFECT_SENTINEL = "__random_effect__";

export const LobbyRulesetSchema = z.enum(["remote_online", "original_party"]);
export type LobbyRuleset = z.infer<typeof LobbyRulesetSchema>;

export const TeamModeSchema = z.enum(["none", "pairs"]);
export type TeamMode = z.infer<typeof TeamModeSchema>;

export const LobbyConfigSchema = z.object({
    ruleset: LobbyRulesetSchema,
    specialRoundsEnabled: z.boolean(),
    teamMode: TeamModeSchema,
    hidePerksUntilTriggered: z.boolean().default(false),
    ttsMode: TtsModeSchema.default("client"),
});
export type LobbyConfig = z.infer<typeof LobbyConfigSchema>;

export const OriginalPartyEffectDefinitionSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    iconName: z.string().default("fas:star"),
    defaultTurns: z.number().int().positive().default(1),
    specialType: z.enum(["hide_half_incorrect_authors"]).optional(),
    onApplyProgram: z.array(z.lazy(() => OriginalPartyRuleStepSchema)).default([]),
    onApplyEditorWorkspaceState: z.record(z.string(), z.any()).optional(),
    onBlockedProgram: z.array(z.lazy(() => OriginalPartyRuleStepSchema)).default([]),
    onBlockedEditorWorkspaceState: z.record(z.string(), z.any()).optional(),
    onEndedProgram: z.array(z.lazy(() => OriginalPartyRuleStepSchema)).default([]),
    onEndedEditorWorkspaceState: z.record(z.string(), z.any()).optional(),
    onClearedProgram: z.array(z.lazy(() => OriginalPartyRuleStepSchema)).default([]),
    onClearedEditorWorkspaceState: z.record(z.string(), z.any()).optional(),
    lifecycleEditorWorkspaceState: z.record(z.string(), z.any()).optional(),
});
export type OriginalPartyEffectDefinition = z.infer<typeof OriginalPartyEffectDefinitionSchema>;

export const OriginalPartyTaskSchema = z.object({
    id: z.string(),
    text: z.string(),
});
export type OriginalPartyTask = z.infer<typeof OriginalPartyTaskSchema>;

export const OriginalPartyTaskPoolSchema = z.object({
    id: z.string(),
    name: z.string(),
    tasks: z.array(OriginalPartyTaskSchema).default([]),
});
export type OriginalPartyTaskPool = z.infer<typeof OriginalPartyTaskPoolSchema>;

export const NumericValueMetricSchema = z.enum([
    "score",
    "immunity",
    "scoreboard_position",
    "correct_guess_count",
    "incorrect_guess_count",
    "team_count",
]);
export type NumericValueMetric = z.infer<typeof NumericValueMetricSchema>;

export const NumericComparisonOperatorSchema = z.enum(["gt", "gte", "lt", "lte", "eq", "neq"]);
export type NumericComparisonOperator = z.infer<typeof NumericComparisonOperatorSchema>;
export const NumericMathOperatorSchema = z.enum(["add", "subtract", "multiply", "divide", "power"]);
export type NumericMathOperator = z.infer<typeof NumericMathOperatorSchema>;

export const BuiltinTeamSelectorSchema = z.enum([
    "self",
    "random_other",
    "leading_other",
    "trailing_other",
    "weighted_leading_other",
    "weighted_trailing_other",
]);
export type BuiltinTeamSelector = z.infer<typeof BuiltinTeamSelectorSchema>;

export const VariableNameSchema = z.string().trim().min(1).max(32);
export type VariableName = z.infer<typeof VariableNameSchema>;
export const TeamVariableNameSchema = VariableNameSchema;
export type TeamVariableName = z.infer<typeof TeamVariableNameSchema>;

export const TeamSelectorSchema = z.union([
    BuiltinTeamSelectorSchema,
    z.object({
        kind: z.literal("variable"),
        variableName: TeamVariableNameSchema,
    }),
    z.object({
        kind: z.literal("weighted_other"),
        bias: z.enum(["leading", "trailing"]),
        strength: z.number().positive().default(1.75),
        includeSelf: z.boolean().default(false),
    }),
]);
export type TeamSelector = z.infer<typeof TeamSelectorSchema>;

export const EffectSelectorSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("specific"),
        effectId: z.string(),
    }),
    z.object({
        kind: z.literal("random_available"),
    }),
    z.object({
        kind: z.literal("random_active"),
    }),
    z.object({
        kind: z.literal("variable"),
        variableName: VariableNameSchema,
    }),
]);
export type EffectSelector = z.infer<typeof EffectSelectorSchema>;

export const ClearEffectSelectorSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("all"),
    }),
    z.object({
        kind: z.literal("specific"),
        effectId: z.string(),
    }),
    z.object({
        kind: z.literal("random_active"),
    }),
    z.object({
        kind: z.literal("variable"),
        variableName: VariableNameSchema,
    }),
]);
export type ClearEffectSelector = z.infer<typeof ClearEffectSelectorSchema>;

export const TaskSelectorSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("specific_pool"),
        taskPoolId: z.string(),
    }),
    z.object({
        kind: z.literal("specific_task"),
        taskId: z.string(),
    }),
    z.object({
        kind: z.literal("variable"),
        variableName: VariableNameSchema,
    }),
]);
export type TaskSelector = z.infer<typeof TaskSelectorSchema>;

export type OriginalPartyTextValue =
    | {
        kind: "literal";
        value: string;
    }
    | {
        kind: "join";
        left: OriginalPartyTextValue;
        right: OriginalPartyTextValue;
    }
    | {
        kind: "team_name";
        teamSelector: TeamSelector;
    }
    | {
        kind: "effect_name";
        effectSelector: EffectSelector;
    }
    | {
        kind: "task_name";
        taskSelector: TaskSelector;
    };

export const OriginalPartyTextValueSchema: z.ZodType<OriginalPartyTextValue> = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("literal"),
        value: z.string().max(220),
    }),
    z.object({
        kind: z.literal("join"),
        left: z.lazy(() => OriginalPartyTextValueSchema),
        right: z.lazy(() => OriginalPartyTextValueSchema),
    }),
    z.object({
        kind: z.literal("team_name"),
        teamSelector: TeamSelectorSchema.default("self"),
    }),
    z.object({
        kind: z.literal("effect_name"),
        effectSelector: EffectSelectorSchema,
    }),
    z.object({
        kind: z.literal("task_name"),
        taskSelector: TaskSelectorSchema,
    }),
]);

export type OriginalPartyNumericValue = 
    | {
        kind: "literal";
        value: number;
    }
    | {
        kind: "team_metric";
        metric: Extract<NumericValueMetric, "score" | "immunity" | "scoreboard_position">;
        teamSelector: TeamSelector;
    }
    | {
        kind: "round_metric";
        metric: Extract<NumericValueMetric, "correct_guess_count" | "incorrect_guess_count" | "team_count">;
    }
    | {
        kind: "math";
        operator: NumericMathOperator;
        left: OriginalPartyNumericValue;
        right: OriginalPartyNumericValue;
    }
    | {
        kind: "sqrt";
        value: OriginalPartyNumericValue;
    };

export const OriginalPartyNumericValueSchema: z.ZodType<OriginalPartyNumericValue> = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("literal"),
        value: z.number(),
    }),
    z.object({
        kind: z.literal("team_metric"),
        metric: z.enum(["score", "immunity", "scoreboard_position"]),
        teamSelector: TeamSelectorSchema.default("self"),
    }),
    z.object({
        kind: z.literal("round_metric"),
        metric: z.enum(["correct_guess_count", "incorrect_guess_count", "team_count"]),
    }),
    z.object({
        kind: z.literal("math"),
        operator: NumericMathOperatorSchema,
        left: z.lazy(() => OriginalPartyNumericValueSchema),
        right: z.lazy(() => OriginalPartyNumericValueSchema),
    }),
    z.object({
        kind: z.literal("sqrt"),
        value: z.lazy(() => OriginalPartyNumericValueSchema),
    }),
]);
export type OriginalPartyBooleanValue =
    | {
        kind: "literal";
        value: boolean;
    }
    | {
        kind: "compare_numbers";
        left: OriginalPartyNumericValue;
        operator: NumericComparisonOperator;
        right: OriginalPartyNumericValue;
    }
    | {
        kind: "has_effect";
        effectSelector:
            | {kind: "any"}
            | {kind: "specific"; effectId: string}
            | {kind: "variable"; variableName: string};
        teamSelector: TeamSelector;
        negate?: boolean;
    }
    | {
        kind: "chance";
        percent: OriginalPartyNumericValue;
    };

export const OriginalPartyBooleanValueSchema: z.ZodType<OriginalPartyBooleanValue> = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("literal"),
        value: z.boolean(),
    }),
    z.object({
        kind: z.literal("compare_numbers"),
        left: OriginalPartyNumericValueSchema,
        operator: NumericComparisonOperatorSchema,
        right: OriginalPartyNumericValueSchema,
    }),
    z.object({
        kind: z.literal("has_effect"),
        effectSelector: z.discriminatedUnion("kind", [
            z.object({
                kind: z.literal("any"),
            }),
            z.object({
                kind: z.literal("specific"),
                effectId: z.string(),
            }),
            z.object({
                kind: z.literal("variable"),
                variableName: VariableNameSchema,
            }),
        ]),
        teamSelector: TeamSelectorSchema.default("self"),
        negate: z.boolean().optional(),
    }),
    z.object({
        kind: z.literal("chance"),
        percent: OriginalPartyNumericValueSchema,
    }),
]);
export const OriginalPartyRuleConditionSchema = OriginalPartyBooleanValueSchema;
export type OriginalPartyRuleCondition = z.infer<typeof OriginalPartyRuleConditionSchema>;

export const RuleActionKindSchema = z.enum([
    "score_delta",
    "apply_effect",
    "remove_effect",
    "clear_effects",
    "assign_task",
    "show_popup",
    "prompt_select_team",
    "prompt_select_effect",
    "prompt_select_task",
    "random_branch",
    "if",
]);
export type RuleActionKind = z.infer<typeof RuleActionKindSchema>;

const RuleActionBaseSchema = z.object({
    id: z.string(),
});

export const ScoreDeltaRuleActionSchema = RuleActionBaseSchema.extend({
    kind: z.literal("score_delta"),
    amount: OriginalPartyNumericValueSchema,
    target: z.enum(["score", "immunity"]).default("score"),
    teamSelector: TeamSelectorSchema.default("self"),
});

export const ApplyEffectRuleActionSchema = RuleActionBaseSchema.extend({
    kind: z.literal("apply_effect"),
    effectSelector: EffectSelectorSchema,
    turns: OriginalPartyNumericValueSchema.optional(),
    ignoreImmunityPoints: OriginalPartyBooleanValueSchema.default({kind: "literal", value: false}),
    teamSelector: TeamSelectorSchema.default("self"),
});

export const RemoveEffectRuleActionSchema = RuleActionBaseSchema.extend({
    kind: z.literal("remove_effect"),
    effectId: z.string(),
});

export const ClearEffectsRuleActionSchema = RuleActionBaseSchema.extend({
    kind: z.literal("clear_effects"),
    effectSelector: ClearEffectSelectorSchema.default({kind: "all"}),
    teamSelector: TeamSelectorSchema.default("self"),
});

export const AssignTaskRuleActionSchema = RuleActionBaseSchema.extend({
    kind: z.literal("assign_task"),
    taskSelector: TaskSelectorSchema,
    teamSelector: TeamSelectorSchema.default("self"),
});

export const ShowPopupRuleActionSchema = RuleActionBaseSchema.extend({
    kind: z.literal("show_popup"),
    title: z.string().trim().min(1).max(80),
    body: z.string().trim().min(1).max(220),
    teamSelector: TeamSelectorSchema.default("self"),
});

export const PromptSelectTeamRuleActionSchema = RuleActionBaseSchema.extend({
    kind: z.literal("prompt_select_team"),
    variableName: TeamVariableNameSchema,
    promptedTeamSelector: TeamSelectorSchema.default("self"),
    title: OriginalPartyTextValueSchema,
    body: OriginalPartyTextValueSchema,
    includeSelf: z.boolean().default(false),
});

export const PromptSelectEffectRuleActionSchema = RuleActionBaseSchema.extend({
    kind: z.literal("prompt_select_effect"),
    variableName: VariableNameSchema,
    promptedTeamSelector: TeamSelectorSchema.default("self"),
    title: OriginalPartyTextValueSchema,
    body: OriginalPartyTextValueSchema,
});

export const PromptSelectTaskRuleActionSchema = RuleActionBaseSchema.extend({
    kind: z.literal("prompt_select_task"),
    variableName: VariableNameSchema,
    promptedTeamSelector: TeamSelectorSchema.default("self"),
    taskSelector: TaskSelectorSchema,
    title: OriginalPartyTextValueSchema,
    body: OriginalPartyTextValueSchema,
});

type OriginalPartyRuleStep =
    | z.infer<typeof ScoreDeltaRuleActionSchema>
    | z.infer<typeof ApplyEffectRuleActionSchema>
    | z.infer<typeof RemoveEffectRuleActionSchema>
    | z.infer<typeof ClearEffectsRuleActionSchema>
    | z.infer<typeof AssignTaskRuleActionSchema>
    | z.infer<typeof ShowPopupRuleActionSchema>
    | z.infer<typeof PromptSelectTeamRuleActionSchema>
    | z.infer<typeof PromptSelectEffectRuleActionSchema>
    | z.infer<typeof PromptSelectTaskRuleActionSchema>
    | {
        id: string;
        kind: "random_branch";
        branches: {
            id: string;
            weight: OriginalPartyNumericValue;
            program: OriginalPartyRuleStep[];
        }[];
    }
    | {
        id: string;
        kind: "if";
        condition: OriginalPartyRuleCondition;
        then: OriginalPartyRuleStep[];
        else?: OriginalPartyRuleStep[];
    };

const OriginalPartyRuleStepSchema: z.ZodType<OriginalPartyRuleStep> = z.lazy(() => z.discriminatedUnion("kind", [
    ScoreDeltaRuleActionSchema,
    ApplyEffectRuleActionSchema,
    RemoveEffectRuleActionSchema,
    ClearEffectsRuleActionSchema,
    AssignTaskRuleActionSchema,
    ShowPopupRuleActionSchema,
    PromptSelectTeamRuleActionSchema,
    PromptSelectEffectRuleActionSchema,
    PromptSelectTaskRuleActionSchema,
    RuleActionBaseSchema.extend({
        kind: z.literal("random_branch"),
        branches: z.array(z.object({
            id: z.string(),
            weight: OriginalPartyNumericValueSchema,
            program: z.array(OriginalPartyRuleStepSchema).default([]),
        })).min(1),
    }),
    RuleActionBaseSchema.extend({
        kind: z.literal("if"),
        condition: OriginalPartyRuleConditionSchema,
        then: z.array(OriginalPartyRuleStepSchema).default([]),
        else: z.array(OriginalPartyRuleStepSchema).optional(),
    }),
]));

export {OriginalPartyRuleStepSchema};
export type {OriginalPartyRuleStep};

export const PERK_EFFECT_TYPE_OPTIONS = [
    "score_delta",
    "manual_only",
] as const;

export const PerkEffectTypeSchema = z.enum(PERK_EFFECT_TYPE_OPTIONS);
export type PerkEffectType = z.infer<typeof PerkEffectTypeSchema>;
export const CURRENT_ORIGINAL_PARTY_PERK_SCHEMA_VERSION = 1;

export const OriginalPartyPerkSchema = z.object({
    id: z.string(),
    schemaVersion: z.number().int().positive().default(CURRENT_ORIGINAL_PARTY_PERK_SCHEMA_VERSION),
    kind: z.enum(["positive", "negative"]),
    title: z.string(),
    description: z.string(),
    program: z.array(OriginalPartyRuleStepSchema).default([]),
    editorWorkspaceState: z.record(z.string(), z.any()).optional(),
    effectType: PerkEffectTypeSchema.optional(),
    scoreDelta: z.number().optional(),
    rounds: z.number().int().positive().optional(),
    manualOnly: z.boolean().optional(),
});
export type OriginalPartyPerk = z.infer<typeof OriginalPartyPerkSchema>;

export const AuthorPerkPairSchema = z.object({
    positivePerk: OriginalPartyPerkSchema,
    negativePerk: OriginalPartyPerkSchema,
});
export type AuthorPerkPair = z.infer<typeof AuthorPerkPairSchema>;

export const OriginalPartyBalanceSettingsSchema = z.object({
    immunityPointCap: z.number().int().positive().nullable().default(null),
    randomTeamRepeatWindow: z.number().int().nonnegative().default(1),
    randomEffectRepeatWindow: z.number().int().nonnegative().default(1),
}).default({
    immunityPointCap: null,
    randomTeamRepeatWindow: 1,
    randomEffectRepeatWindow: 1,
});
export type OriginalPartyBalanceSettings = z.infer<typeof OriginalPartyBalanceSettingsSchema>;

export const OriginalPartyAssetsSchema = z.object({
    balance: OriginalPartyBalanceSettingsSchema.default({
        immunityPointCap: null,
        randomTeamRepeatWindow: 1,
        randomEffectRepeatWindow: 1,
    }),
    effects: z.array(OriginalPartyEffectDefinitionSchema).default([]),
    taskPools: z.array(OriginalPartyTaskPoolSchema).default([]),
});
export type OriginalPartyAssets = z.infer<typeof OriginalPartyAssetsSchema>;

export const ActiveOriginalPartyEffectSchema = z.object({
    id: z.string(),
    effectId: z.string(),
    name: z.string(),
    description: z.string(),
    iconName: z.string().default("fas:star"),
    specialType: z.enum(["hide_half_incorrect_authors"]).optional(),
    remainingTurns: z.number().int().positive(),
});
export type ActiveOriginalPartyEffect = z.infer<typeof ActiveOriginalPartyEffectSchema>;

export const AssignedOriginalPartyTaskSchema = z.object({
    id: z.string(),
    poolId: z.string(),
    poolName: z.string(),
    taskId: z.string(),
    text: z.string(),
    completed: z.boolean().default(false),
    assignedAtRound: z.number().int().nonnegative(),
    completedAtRound: z.number().int().nonnegative().nullable().optional(),
});
export type AssignedOriginalPartyTask = z.infer<typeof AssignedOriginalPartyTaskSchema>;

export const OriginalPartyRevealEventSchema = z.discriminatedUnion("kind", [
    z.object({
        id: z.string(),
        kind: z.literal("reveal_perk"),
        teamId: z.string(),
        teamName: z.string(),
        authorId: z.string(),
        authorName: z.string(),
        perkKind: z.enum(["positive", "negative"]),
        perkTitle: z.string(),
        perkDescription: z.string(),
    }),
    z.object({
        id: z.string(),
        kind: z.literal("remove_effect"),
        teamId: z.string(),
        teamName: z.string(),
        sourceTeamId: z.string().optional(),
        sourceTeamName: z.string().optional(),
        effectId: z.string(),
        effectName: z.string(),
        effectDescription: z.string(),
        iconName: z.string().default("fas:star"),
    }),
    z.object({
        id: z.string(),
        kind: z.literal("clear_effects"),
        teamId: z.string(),
        teamName: z.string(),
        sourceTeamId: z.string().optional(),
        sourceTeamName: z.string().optional(),
        clearedCount: z.number().int().nonnegative(),
    }),
    z.object({
        id: z.string(),
        kind: z.literal("apply_effect"),
        teamId: z.string(),
        teamName: z.string(),
        sourceTeamId: z.string().optional(),
        sourceTeamName: z.string().optional(),
        effectId: z.string(),
        effectName: z.string(),
        effectDescription: z.string(),
        iconName: z.string().default("fas:star"),
        turns: z.number().int().positive(),
    }),
    z.object({
        id: z.string(),
        kind: z.literal("block_effect"),
        teamId: z.string(),
        teamName: z.string(),
        sourceTeamId: z.string().optional(),
        sourceTeamName: z.string().optional(),
        effectId: z.string(),
        effectName: z.string(),
        effectDescription: z.string(),
        iconName: z.string().default("fas:star"),
        immunityPointsLeft: z.number().int().nonnegative(),
    }),
    z.object({
        id: z.string(),
        kind: z.literal("assign_task"),
        teamId: z.string(),
        teamName: z.string(),
        sourceTeamId: z.string().optional(),
        sourceTeamName: z.string().optional(),
        taskId: z.string(),
        poolId: z.string(),
        poolName: z.string(),
        taskText: z.string(),
    }),
    z.object({
        id: z.string(),
        kind: z.literal("show_popup"),
        teamId: z.string(),
        teamName: z.string(),
        sourceTeamId: z.string().optional(),
        sourceTeamName: z.string().optional(),
        title: z.string(),
        body: z.string(),
    }),
]);
export type OriginalPartyRevealEvent = z.infer<typeof OriginalPartyRevealEventSchema>;

export const OriginalPartySelectionPromptSchema = z.discriminatedUnion("kind", [
    z.object({
        id: z.string(),
        continuationId: z.string(),
        kind: z.literal("select_team"),
        sourceTeamId: z.string(),
        sourceTeamName: z.string(),
        promptedTeamId: z.string(),
        promptedTeamName: z.string(),
        variableName: TeamVariableNameSchema,
        title: z.string(),
        body: z.string(),
        options: z.array(z.object({
            id: z.string(),
            label: z.string(),
            teamId: z.string(),
            teamName: z.string(),
        })).min(1),
    }),
    z.object({
        id: z.string(),
        continuationId: z.string(),
        kind: z.literal("select_effect"),
        sourceTeamId: z.string(),
        sourceTeamName: z.string(),
        promptedTeamId: z.string(),
        promptedTeamName: z.string(),
        variableName: VariableNameSchema,
        title: z.string(),
        body: z.string(),
        options: z.array(z.object({
            id: z.string(),
            label: z.string(),
            effectId: z.string(),
            effectName: z.string(),
            effectDescription: z.string().optional(),
        })).min(1),
    }),
    z.object({
        id: z.string(),
        continuationId: z.string(),
        kind: z.literal("select_task"),
        sourceTeamId: z.string(),
        sourceTeamName: z.string(),
        promptedTeamId: z.string(),
        promptedTeamName: z.string(),
        variableName: VariableNameSchema,
        taskSelector: TaskSelectorSchema,
        title: z.string(),
        body: z.string(),
        options: z.array(z.object({
            id: z.string(),
            label: z.string(),
            taskId: z.string(),
            taskText: z.string(),
            poolName: z.string().optional(),
        })).min(1),
    }),
]);
export type OriginalPartySelectionPrompt = z.infer<typeof OriginalPartySelectionPromptSchema>;

export const TeamStateSchema = z.object({
    id: z.string(),
    name: z.string(),
    memberPlayerIds: z.array(z.string()).min(1).max(2),
    score: z.number(),
    votedAuthorId: z.string().nullable(),
    readyForNext: z.boolean().optional(),
    activePerk: OriginalPartyPerkSchema.nullable().optional(),
    pendingPerk: OriginalPartyPerkSchema.nullable().optional(),
    submitted: z.boolean().optional(),
    immunityPoints: z.number().int().nonnegative().default(0),
    activeEffects: z.array(ActiveOriginalPartyEffectSchema).default([]),
    assignedTasks: z.array(AssignedOriginalPartyTaskSchema).default([]),
    taskHistory: z.array(z.string()).default([]),
});
export type TeamState = z.infer<typeof TeamStateSchema>;

export const LobbyTeamSetupSchema = z.object({
    id: z.string(),
    name: z.string(),
    memberPlayerIds: z.array(z.string()).min(1).max(2),
});
export type LobbyTeamSetup = z.infer<typeof LobbyTeamSetupSchema>;

export const DEFAULT_REMOTE_LOBBY_CONFIG: LobbyConfig = {
    ruleset: "remote_online",
    specialRoundsEnabled: true,
    teamMode: "none",
    hidePerksUntilTriggered: false,
    ttsMode: "client",
};

export const DEFAULT_ORIGINAL_LOBBY_CONFIG: LobbyConfig = {
    ruleset: "original_party",
    specialRoundsEnabled: false,
    teamMode: "pairs",
    hidePerksUntilTriggered: false,
    ttsMode: "client",
};

export const DEFAULT_ORIGINAL_PARTY_ASSETS: OriginalPartyAssets = {
    balance: {
        immunityPointCap: null,
        randomTeamRepeatWindow: 1,
        randomEffectRepeatWindow: 1,
    },
    effects: [],
    taskPools: [],
};
export const DEFAULT_ORIGINAL_PARTY_EFFECT_ICON = "fas:star";

export function createDefaultOriginalPartyPerk(kind: "positive" | "negative"): OriginalPartyPerk {
    return {
        id: crypto.randomUUID(),
        schemaVersion: CURRENT_ORIGINAL_PARTY_PERK_SCHEMA_VERSION,
        kind,
        title: kind === "positive" ? "Positive perk" : "Negative perk",
        description: "",
        program: [],
        manualOnly: true,
    };
}

export function hasMeaningfulOriginalPartyPerk(perk: OriginalPartyPerk | null | undefined) {
    return Boolean(perk && perk.program.length > 0)
}

export function formatTeamSelectorForDisplay(selector: TeamSelector) {
    if (typeof selector !== "string") {
        if (selector.kind === "variable") {
            return `the selected team (${selector.variableName})`;
        }
        return `a ${selector.bias}-weighted ${selector.includeSelf ? "team" : "other team"} (strength ${selector.strength})`;
    }
    switch (selector) {
        case "random_other": return "a random other team";
        case "leading_other": return "the leading team";
        case "trailing_other": return "the trailing team";
        case "weighted_leading_other": return "a leader-weighted other team";
        case "weighted_trailing_other": return "a trailer-weighted other team";
        case "self":
        default:
            return "this team";
    }
}

function describeRuleStepForDisplay(step: OriginalPartyRuleStep, assets: OriginalPartyAssets): string {
    if (step.kind === "score_delta") {
        const target = (step.target ?? "score") === "immunity" ? "immunity" : "score";
        return `Adjust ${target} by ${formatNumericValueForDisplay(step.amount)} for ${formatTeamSelectorForDisplay(step.teamSelector ?? "self")}`;
    }

    if (step.kind === "apply_effect") {
        const effectSelector = step.effectSelector
        const effectName = effectSelector.kind === "random_available"
            ? "a random effect"
            : effectSelector.kind === "random_active"
                ? "a random active effect"
            : effectSelector.kind === "specific"
                ? (assets.effects.find((effect) => effect.id === effectSelector.effectId)?.name || "the selected effect")
                : `the saved effect (${effectSelector.variableName})`;
        const ignoreImmunitySummary = step.ignoreImmunityPoints.kind === "literal"
            ? (step.ignoreImmunityPoints.value ? ", ignoring immunity" : "")
            : `, ignoring immunity when ${formatConditionForDisplay(step.ignoreImmunityPoints, assets)}`;
        const durationSummary = step.turns
            ? `for ${formatNumericValueForDisplay(step.turns)} turn(s)`
            : "for its default duration";
        return `Apply ${effectName} to ${formatTeamSelectorForDisplay(step.teamSelector ?? "self")} ${durationSummary}${ignoreImmunitySummary}`;
    }

    if (step.kind === "remove_effect") {
        const effectName = assets.effects.find((effect) => effect.id === step.effectId)?.name || "the selected effect";
        return `Remove ${effectName}`;
    }

    if (step.kind === "clear_effects") {
        const effectSelector = step.effectSelector
        if (effectSelector.kind === "specific" && effectSelector.effectId) {
            const effectName = assets.effects.find((effect) => effect.id === effectSelector.effectId)?.name || "the selected effect";
            return `Clear ${effectName} from ${formatTeamSelectorForDisplay(step.teamSelector ?? "self")}`;
        }
        if (effectSelector.kind === "random_active") {
            return `Clear a random active effect from ${formatTeamSelectorForDisplay(step.teamSelector ?? "self")}`;
        }
        return `Clear all effects from ${formatTeamSelectorForDisplay(step.teamSelector ?? "self")}`;
    }

    if (step.kind === "assign_task") {
        const selector = step.taskSelector;
        const taskSummary = selector.kind === "specific_pool"
            ? `a task from ${assets.taskPools.find((pool) => pool.id === selector.taskPoolId)?.name || "the selected pool"}`
            : selector.kind === "specific_task"
                ? (assets.taskPools.flatMap((pool) => pool.tasks).find((task) => task.id === selector.taskId)?.text || "the selected task")
                : `the selected task (${selector.variableName})`;
        return `Assign ${taskSummary} to ${formatTeamSelectorForDisplay(step.teamSelector ?? "self")}`;
    }

    if (step.kind === "show_popup") {
        return `Show popup "${step.title}" linked to ${formatTeamSelectorForDisplay(step.teamSelector ?? "self")}`;
    }

    if (step.kind === "prompt_select_team") {
        const audience = formatTeamSelectorForDisplay(step.promptedTeamSelector ?? "self");
        return `Ask ${audience} to choose a team and save it as ${step.variableName}`;
    }

    if (step.kind === "prompt_select_effect") {
        const audience = formatTeamSelectorForDisplay(step.promptedTeamSelector ?? "self");
        return `Ask ${audience} to choose an effect and save it as ${step.variableName}`;
    }

    if (step.kind === "prompt_select_task") {
        const audience = formatTeamSelectorForDisplay(step.promptedTeamSelector ?? "self");
        const selector = step.taskSelector;
        const taskSummary = selector.kind === "specific_pool"
            ? `a task from ${assets.taskPools.find((pool) => pool.id === selector.taskPoolId)?.name || "the selected pool"}`
            : selector.kind === "specific_task"
                ? (assets.taskPools.flatMap((pool) => pool.tasks).find((task) => task.id === selector.taskId)?.text || "the selected task")
                : `the saved task (${selector.variableName})`;
        return `Ask ${audience} to choose ${taskSummary} and save it as ${step.variableName}`;
    }

    if (step.kind === "if") {
        const branch = step.then.map((child) => describeRuleStepForDisplay(child, assets)).filter(Boolean).join(", ");
        return `If ${formatConditionForDisplay(step.condition, assets)}, ${branch || "do nothing"}`;
    }

    if (step.kind === "random_branch") {
        const branchSummary = step.branches
            .map((branch) => {
                const summary = branch.program.map((child) => describeRuleStepForDisplay(child, assets)).filter(Boolean).join(", ");
                return `${formatNumericValueForDisplay(branch.weight)}: ${summary || "nothing"}`;
            })
            .join("; ");
        return `Randomly pick one: ${branchSummary}`;
    }

    return "";
}

function formatConditionForDisplay(condition: OriginalPartyRuleCondition, assets: OriginalPartyAssets): string {
    if (condition.kind === "chance") {
        return `${formatNumericValueForDisplay(condition.percent)}% chance`;
    }
    if (condition.kind === "literal") {
        return condition.value ? "true" : "false";
    }
    if (condition.kind === "has_effect") {
        const effectSelector = condition.effectSelector;
        const effectName = effectSelector.kind === "any"
            ? "any effect"
            : effectSelector.kind === "specific"
                ? (assets.effects.find((effect) => effect.id === effectSelector.effectId)?.name || "the selected effect")
                : `the saved effect (${effectSelector.variableName})`;
        return `${formatTeamSelectorForDisplay(condition.teamSelector ?? "self")} ${condition.negate ? "does not have" : "has"} ${effectName}`;
    }

    const operator = (() => {
        switch (condition.operator) {
            case "gt": return ">";
            case "gte": return ">=";
            case "lt": return "<";
            case "lte": return "<=";
            case "eq": return "==";
            case "neq": return "!=";
        }
    })();
    return `${formatNumericValueForDisplay(condition.left)} ${operator} ${formatNumericValueForDisplay(condition.right)}`;
}

function formatNumericValueForDisplay(value: OriginalPartyNumericValue): string {
    if (value.kind === "literal") {
        return String(value.value);
    }
    if (value.kind === "team_metric") {
        const metricLabel = value.metric === "scoreboard_position"
            ? "scoreboard position"
            : value.metric;
        return `${formatTeamSelectorForDisplay(value.teamSelector ?? "self")} ${metricLabel}`;
    }
    if (value.kind === "math") {
        const operator = value.operator === "add"
            ? "+"
            : value.operator === "subtract"
                ? "-"
                : value.operator === "multiply"
                    ? "*"
                    : value.operator === "divide"
                        ? "/"
                        : "**";
        return `(${formatNumericValueForDisplay(value.left)} ${operator} ${formatNumericValueForDisplay(value.right)})`;
    }
    if (value.kind === "sqrt") {
        return `sqrt(${formatNumericValueForDisplay(value.value)})`;
    }
    switch (value.metric) {
        case "correct_guess_count": return "correct guesses";
        case "incorrect_guess_count": return "incorrect guesses";
        case "team_count": return "number of teams";
    }
}

function formatTextValueForDisplay(value: OriginalPartyTextValue): string {
    switch (value.kind) {
        case "literal":
            return value.value;
        case "join":
            return `${formatTextValueForDisplay(value.left)}${formatTextValueForDisplay(value.right)}`;
        case "team_name":
            return `${formatTeamSelectorForDisplay(value.teamSelector ?? "self")} name`;
        case "effect_name":
            if (value.effectSelector.kind === "specific") return "effect name";
            if (value.effectSelector.kind === "variable") return `saved effect ${value.effectSelector.variableName}`;
            return value.effectSelector.kind === "random_active" ? "random active effect" : "random effect";
        case "task_name":
            if (value.taskSelector.kind === "specific_task") return "task text";
            if (value.taskSelector.kind === "specific_pool") return "task pool name";
            return `saved task ${value.taskSelector.variableName}`;
    }
}

export function getOriginalPartyPerkDisplayDescription(
    perk: OriginalPartyPerk | null | undefined,
    assets: OriginalPartyAssets = DEFAULT_ORIGINAL_PARTY_ASSETS
) {
    if (!perk) return "";
    const authoredDescription = perk.description?.trim();
    if (authoredDescription) return authoredDescription;
    if (!hasMeaningfulOriginalPartyPerk(perk)) return "";
    return perk.program
        .map((step) => describeRuleStepForDisplay(step, assets))
        .filter(Boolean)
        .join(". ");
}

export function createDefaultOriginalPartyAssets(): OriginalPartyAssets {
    return {
        balance: {
            immunityPointCap: null,
            randomTeamRepeatWindow: 1,
            randomEffectRepeatWindow: 1,
        },
        effects: [],
        taskPools: [],
    };
}

export function normalizeOriginalPartyPerk(perk: OriginalPartyPerk): OriginalPartyPerk {
    const normalizeStep = (step: OriginalPartyRuleStep): OriginalPartyRuleStep => {
        if (step.kind === "score_delta") {
            return {
                ...step,
                amount: typeof step.amount === "number" ? {kind: "literal", value: step.amount} : step.amount,
                target: step.target ?? "score",
                teamSelector: step.teamSelector ?? "self",
            };
        }
        if (step.kind === "apply_effect") {
            const legacyStep = step as typeof step & {effectId?: string; ignoreImmunityPoints?: boolean | OriginalPartyRuleCondition}
            return {
                ...step,
                effectSelector: step.effectSelector ?? (legacyStep.effectId === RANDOM_EFFECT_SENTINEL
                    ? {kind: "random_available"}
                    : {kind: "specific", effectId: legacyStep.effectId || ""}),
                turns: typeof step.turns === "number" ? {kind: "literal", value: step.turns} : step.turns,
                ignoreImmunityPoints: typeof legacyStep.ignoreImmunityPoints === "boolean"
                    ? {kind: "literal", value: legacyStep.ignoreImmunityPoints}
                    : (step.ignoreImmunityPoints ?? {kind: "literal", value: false}),
                teamSelector: step.teamSelector ?? "self",
            };
        }
        if (step.kind === "remove_effect") {
            return {
                id: step.id,
                kind: "clear_effects",
                effectSelector: {kind: "specific", effectId: step.effectId || ""},
                teamSelector: "self",
            };
        }
        if (step.kind === "clear_effects") {
            const legacyStep = step as typeof step & {target?: "all" | "specific"; effectId?: string}
            return {
                ...step,
                effectSelector: step.effectSelector ?? (legacyStep.target === "specific" && legacyStep.effectId
                    ? {kind: "specific", effectId: legacyStep.effectId}
                    : {kind: "all"}),
                teamSelector: step.teamSelector ?? "self",
            };
        }
        if (step.kind === "assign_task") {
            const legacyStep = step as typeof step & {taskPoolId?: string}
            return {
                ...step,
                taskSelector: step.taskSelector ?? {kind: "specific_pool", taskPoolId: legacyStep.taskPoolId || ""},
                teamSelector: step.teamSelector ?? "self",
            };
        }
        if (step.kind === "show_popup") {
            return {
                ...step,
                teamSelector: step.teamSelector ?? "self",
            };
        }
        if (step.kind === "prompt_select_team") {
            return {
                ...step,
                promptedTeamSelector: step.promptedTeamSelector ?? "self",
                title: typeof step.title === "string" ? {kind: "literal", value: step.title} : step.title,
                body: typeof step.body === "string" ? {kind: "literal", value: step.body} : step.body,
                includeSelf: step.includeSelf ?? false,
            };
        }
        if (step.kind === "prompt_select_effect") {
            return {
                ...step,
                promptedTeamSelector: step.promptedTeamSelector ?? "self",
                title: typeof step.title === "string" ? {kind: "literal", value: step.title} : step.title,
                body: typeof step.body === "string" ? {kind: "literal", value: step.body} : step.body,
            };
        }
        if (step.kind === "prompt_select_task") {
            const legacyStep = step as typeof step & {taskPoolId?: string}
            return {
                ...step,
                promptedTeamSelector: step.promptedTeamSelector ?? "self",
                taskSelector: step.taskSelector ?? {kind: "specific_pool", taskPoolId: legacyStep.taskPoolId || ""},
                title: typeof step.title === "string" ? {kind: "literal", value: step.title} : step.title,
                body: typeof step.body === "string" ? {kind: "literal", value: step.body} : step.body,
            };
        }
        if (step.kind === "if") {
            const legacyCondition = step.condition as unknown as {
                kind?: "compare_number";
                field?: "team_score" | "leader_score" | "correct_guess_count" | "incorrect_guess_count";
                operator?: NumericComparisonOperator;
                value?: number;
            }
            const hasEffectCondition = step.condition.kind === "has_effect"
                ? (() => {
                    const legacyHasEffectCondition = step.condition as typeof step.condition & {effectId?: string}
                    return {
                        ...step.condition,
                        effectSelector: "effectSelector" in step.condition
                            ? step.condition.effectSelector
                            : legacyHasEffectCondition.effectId
                                ? {kind: "specific" as const, effectId: legacyHasEffectCondition.effectId}
                                : {kind: "any" as const},
                        teamSelector: step.condition.teamSelector ?? "self",
                    };
                })()
                : null
            const normalizedCondition: OriginalPartyRuleCondition = legacyCondition.kind === "compare_number"
                ? {
                    kind: "compare_numbers",
                    left: normalizeLegacyNumericConditionField(legacyCondition.field),
                    operator: legacyCondition.operator ?? "gte",
                    right: {
                        kind: "literal",
                        value: legacyCondition.value ?? 0,
                    },
                }
                : hasEffectCondition
                    ? hasEffectCondition
                    : step.condition.kind === "chance" && typeof step.condition.percent === "number"
                        ? {
                            ...step.condition,
                            percent: {kind: "literal", value: step.condition.percent},
                        }
                        : step.condition
            return {
                ...step,
                condition: normalizedCondition,
                then: step.then.map(normalizeStep),
                else: step.else?.map(normalizeStep) ?? [],
            };
        }
        if (step.kind === "random_branch") {
            return {
                ...step,
                branches: step.branches.map((branch) => ({
                    ...branch,
                    weight: typeof branch.weight === "number" ? {kind: "literal", value: branch.weight} : branch.weight,
                    program: branch.program.map(normalizeStep),
                })),
            };
        }
        return step;
    };

    if (perk.program.length > 0) {
        return {
            ...perk,
            schemaVersion: perk.schemaVersion ?? CURRENT_ORIGINAL_PARTY_PERK_SCHEMA_VERSION,
            title: perk.title ?? "",
            description: perk.description ?? "",
            editorWorkspaceState: perk.editorWorkspaceState,
            program: perk.program.map(normalizeStep),
        };
    }

    const program: OriginalPartyRuleStep[] = [];
    if (perk.effectType === "score_delta") {
        program.push({
            id: crypto.randomUUID(),
            kind: "score_delta",
            amount: {kind: "literal", value: perk.scoreDelta ?? 0},
            target: "score",
            teamSelector: "self",
        });
    }

    return {
        ...perk,
        schemaVersion: perk.schemaVersion ?? CURRENT_ORIGINAL_PARTY_PERK_SCHEMA_VERSION,
        title: perk.title ?? "",
        description: perk.description ?? "",
        editorWorkspaceState: perk.editorWorkspaceState,
        program: program.map(normalizeStep),
    };
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseStoredOriginalPartyPerkValue(value: string | null | undefined, kind: "positive" | "negative"): OriginalPartyPerk {
    if (!value) return createDefaultOriginalPartyPerk(kind);
    const raw = JSON.parse(value) as unknown;
    if (!isPlainRecord(raw)) {
        throw new Error("Stored perk payload is not an object");
    }
    const normalized = normalizeOriginalPartyPerk({
        ...createDefaultOriginalPartyPerk(kind),
        ...raw,
        kind,
    } as OriginalPartyPerk);
    return OriginalPartyPerkSchema.parse(normalized);
}

export function createDefaultEffectDefinition(): OriginalPartyEffectDefinition {
    return {
        id: crypto.randomUUID(),
        name: "New effect",
        description: "",
        iconName: DEFAULT_ORIGINAL_PARTY_EFFECT_ICON,
        defaultTurns: 1,
        specialType: undefined,
        onApplyProgram: [],
        onApplyEditorWorkspaceState: undefined,
        onBlockedProgram: [],
        onBlockedEditorWorkspaceState: undefined,
        onEndedProgram: [],
        onEndedEditorWorkspaceState: undefined,
        onClearedProgram: [],
        onClearedEditorWorkspaceState: undefined,
        lifecycleEditorWorkspaceState: undefined,
    };
}

export function createDefaultTaskPool(): OriginalPartyTaskPool {
    return {
        id: crypto.randomUUID(),
        name: "New task pool",
        tasks: [],
    };
}

export function createDefaultTask(): OriginalPartyTask {
    return {
        id: crypto.randomUUID(),
        text: "",
    };
}

export function createDefaultRuleStep(kind: Exclude<RuleActionKind, "if"> = "score_delta"): OriginalPartyRuleStep {
    switch (kind) {
        case "apply_effect":
            return {
                id: crypto.randomUUID(),
                kind,
                effectSelector: {kind: "specific", effectId: ""},
                turns: undefined,
                ignoreImmunityPoints: {kind: "literal", value: false},
                teamSelector: "self",
            };
        case "remove_effect":
            return {id: crypto.randomUUID(), kind, effectId: ""};
        case "clear_effects":
            return {id: crypto.randomUUID(), kind, effectSelector: {kind: "all"}, teamSelector: "self"};
        case "assign_task":
            return {
                id: crypto.randomUUID(),
                kind,
                taskSelector: {kind: "specific_pool", taskPoolId: ""},
                teamSelector: "self",
            };
        case "random_branch":
            return {
                id: crypto.randomUUID(),
                kind,
                branches: [
                    {id: crypto.randomUUID(), weight: {kind: "literal", value: 50}, program: []},
                    {id: crypto.randomUUID(), weight: {kind: "literal", value: 50}, program: []},
                ],
            };
        case "show_popup":
            return {
                id: crypto.randomUUID(),
                kind,
                title: "Big news",
                body: "Something just happened.",
                teamSelector: "self",
            };
        case "prompt_select_team":
            return {
                id: crypto.randomUUID(),
                kind,
                variableName: "chosen_team",
                promptedTeamSelector: "self",
                title: {kind: "literal", value: "Choose a team"},
                body: {kind: "literal", value: "Pick which team this perk should affect."},
                includeSelf: false,
            };
        case "prompt_select_effect":
            return {
                id: crypto.randomUUID(),
                kind,
                variableName: "chosen_effect",
                promptedTeamSelector: "self",
                title: {kind: "literal", value: "Choose an effect"},
                body: {kind: "literal", value: "Pick which effect this perk should use."},
            };
        case "prompt_select_task":
            return {
                id: crypto.randomUUID(),
                kind,
                variableName: "chosen_task",
                promptedTeamSelector: "self",
                taskSelector: {kind: "specific_pool", taskPoolId: ""},
                title: {kind: "literal", value: "Choose a task"},
                body: {kind: "literal", value: "Pick which task this perk should assign."},
            };
        case "score_delta":
        default:
            return {id: crypto.randomUUID(), kind: "score_delta", amount: {kind: "literal", value: 0}, target: "score", teamSelector: "self"};
    }
}

export function createDefaultIfRuleStep(): OriginalPartyRuleStep {
    return {
        id: crypto.randomUUID(),
        kind: "if",
        condition: {
            kind: "compare_numbers",
            left: {
                kind: "team_metric",
                metric: "score",
                teamSelector: "self",
            },
            operator: "gte",
            right: {
                kind: "literal",
                value: 0,
            },
        },
        then: [],
        else: [],
    };
}

function normalizeLegacyNumericConditionField(field?: "team_score" | "leader_score" | "correct_guess_count" | "incorrect_guess_count"): OriginalPartyNumericValue {
    switch (field) {
        case "leader_score":
            return {kind: "team_metric", metric: "score", teamSelector: "leading_other"};
        case "correct_guess_count":
            return {kind: "round_metric", metric: "correct_guess_count"};
        case "incorrect_guess_count":
            return {kind: "round_metric", metric: "incorrect_guess_count"};
        case "team_score":
        default:
            return {kind: "team_metric", metric: "score", teamSelector: "self"};
    }
}
